import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import { PrimaryButton, Text, Colors, SecondaryButton, OutlinedDropdown } from 'library';
import { client, consoleLogInDev, track, isValidZipCode, isValidFormInputString } from 'shared';
import { useUserContext } from 'UserContext';
import TextInput from './TextInput';
import useContact from 'parent-portal/profile/BusinessProfile/hooks/useContact';
import SelectOrCreateContact from './SelectOrCreateContact';
import { states } from 'parent-portal/signup/states';

const defaultNewContact = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    contactPreference: 'EMAIL',
};

export default function AddBusinessLocationForm({
    cancel,
    complete,
    optionalContact,
}: {
    cancel: () => void;
    complete?: () => void;
    optionalContact?: boolean;
}) {
    const { refreshAlreadyLoggedInUser, getDisplayText } = useUserContext();
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('Select one');
    const [zip, setZip] = useState('');
    const [description, setDescription] = useState('');
    const [programId, setProgramId] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [existingContact, setExistingContact] = useState(-1);
    const {
        isNewContactValid,
        mainContactInfo,
        updateMainContactInfo,
        populateCurrentContactInfo,
        submissionData,
    } = useContact(defaultNewContact, optionalContact);
    function isAddValid() {
        return isNewContactValid() && city && state && state !== 'Select one' && zip && isValidZipCode(zip);
    }

    function onAddAddress() {
        let data = existingContact === -1 ? submissionData : { user_id: existingContact };
        client('api/business-location/', {
            body: {
                street,
                city,
                state,
                zip_code: zip,
                location_name: description,
                program_id: programId,
                ...data,
            },
        })
            .then((res) => {
                if (res.status === 500) {
                    setErrorMessage(res.message);
                } else {
                    refreshAlreadyLoggedInUser();
                    onCancel();
                }
            })
            .catch((error) => {
                consoleLogInDev(error);
                track('Add location Error', { error });
            });
    }

    function onCancel() {
        complete?.();
        setStreet('');
        setCity('');
        setState('Select one');
        setZip('');
        setDescription('');
        populateCurrentContactInfo(defaultNewContact);
        cancel();
    }

    const stateOptions = [
        { key: 'Select one', value: 'Select one' },
        ...states
            .filter((x) => x)
            .map((x) => {
                return { key: x, value: x };
            }),
    ];

    return (
        <Grid container style={{ padding: 30 }}>
            <Text variant="display"> Add a {getDisplayText('center').capitalize()} Location</Text>
            <Grid item xs={12}>
                <TextInput label="Location Name" value={description} onChange={(val) => setDescription(val)} />
            </Grid>
            <Grid item xs={12}>
                <TextInput label="Address*" value={street} onChange={(val) => setStreet(val)} />
            </Grid>
            <Grid item xs={12}>
                <TextInput label="City*" value={city} onChange={(val) => setCity(val)} />
            </Grid>
            <Grid item xs={12} md={6}>
                <Text variant="body1" bold={true}>
                    State*
                </Text>
                <OutlinedDropdown
                    id="state"
                    required
                    error={state !== 'Select one' && !isValidFormInputString(state)}
                    value={state}
                    onChange={(event: any) => setState(event?.target.value)}
                    fields={stateOptions}
                    autoComplete="address-level1"
                    style={{ minWidth: 150 }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput label="Zip*" value={zip} onChange={(val) => setZip(val)} />
            </Grid>
            <Grid item xs={12}>
                <TextInput label="Program Id" value={programId} onChange={(val) => setProgramId(val)} />
            </Grid>
            <Grid item xs={12}>
                <SelectOrCreateContact
                    mainContactInfo={mainContactInfo}
                    updateMainContactInfo={updateMainContactInfo}
                    setExistingContact={setExistingContact}
                    existingContact={existingContact}
                    errorMessage={errorMessage}
                />
            </Grid>
            <Grid container item xs={12} justify="space-around">
                <SecondaryButton buttonStyle={{ maxWidth: '30%' }} onClick={onCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={!isAddValid()} buttonStyle={{ maxWidth: '30%' }} onClick={onAddAddress}>
                    Add
                </PrimaryButton>
            </Grid>
        </Grid>
    );
}
